/**
 * MechaGuard - OpsPanel（运营调参浮层）
 *
 * 仅当地址栏带 ?ops=1 时由 main 初始化。提供一个固定在右侧的可滚动面板，
 * 让运营人员直接调整塔 / 敌人 / 波次的数值，改动实时套用到当前对局
 * （塔立即生效；敌人/波次对下一波或新刷怪生效），并持久化到 localStorage。
 *
 * 面板自带样式（注入 <style>），不依赖修改主样式表。
 *
 * 快捷键：` 键开关面板。也可点左下角 ⚙ 悬浮按钮。
 */

import { TOWER_TYPES } from '../tower-types/index.js';
import { EnemyTypes } from '../enemy-types/index.js';
import { WAVE_DEFAULTS } from '../systems/WaveSystem.js';

/** 波次字段中文标签 */
const WAVE_LABELS = {
  totalWaves: '总波数',
  enemyCountScale: '出怪数量倍率',
  hpGrowthPerWave: '每波生命成长',
  prepTime: '备战时长(秒)',
  bossEvery: 'BOSS波间隔',
  bonusBase: '通关基础奖励',
  bonusPerWave: '每波额外奖励',
  intervalScale: '刷怪间隔系数',
};

/** 波次字段悬浮提示（含原始 JSON 字段名，便于对照预设） */
const WAVE_HINTS = {
  totalWaves: '整局全部关卡的总波数；设定后覆盖各关卡自带的默认值（地图里多为 30）',
  enemyCountScale: '每波各组怪物数量的整体倍率：1=原样，2=翻倍，0.5=减半（保底每波每组 1 只）',
  hpGrowthPerWave: '每波敌人生命相对上一波的成长倍率，0.10 即第 30 波约为 3.9 倍',
  prepTime: '每波开打前的备战倒计时（秒）',
  bossEvery: '每隔多少波出现一次 BOSS 波',
  bonusBase: '通关时固定的基础奖励金币',
  bonusPerWave: '每波额外奖励 = 基数 + 波号 × 该值',
  intervalScale: '刷怪间隔整体缩放：>1 更慢、<1 更密集',
};

/** 全局战斗数值（技能相关）字段中文标签与说明 */
const GLOBAL_INFO = {
  orbitalDamage: { label: '轨道打击伤害', hint: '轨道打击命中点对主目标的伤害；后期重装/精英建议 ≥1000' },
  orbitalRadius: { label: '轨道打击半径', hint: '轨道打击作用范围（像素），瞄准圈同步变化' },
  shieldAmount: { label: '应急护盾量', hint: '每次释放应急护盾提供的护盾点' },
  empFreezeDuration: { label: 'EMP冻结时长', hint: 'EMP 冻结全场敌人的持续时长（秒）' },
};

/** 塔 / 敌人 字段中文标签与说明（按原始 key 索引） */
const FIELD_INFO = {
  // —— 塔通用 ——
  cost: { label: '售价', hint: '建造该塔消耗的初始金币；商店排序与升级费用均基于它' },
  baseRange: { label: '射程', hint: '塔能命中敌人中心的最大距离' },
  baseDamage: { label: '基础伤害', hint: '每次攻击的基准伤害（未计入等级/技能加成）' },
  fireInterval: { label: '攻击间隔', hint: '两次攻击之间的冷却时间（秒），越小 DPS 越高' },
  barrelLength: { label: '炮管长度', hint: '仅影响渲染表现' },
  turnSpeed: { label: '转向速度', hint: '炮塔转向目标的速度（弧度/秒）' },
  firingFx: { label: '开火后坐', hint: '开火后坐动画时长（秒），仅表现' },
  secondaryFactor: { label: '副束伤害比', hint: '激光满级副束伤害 = 主伤害 × 该比例' },
  // —— 冰冻塔 ——
  slowFactor: { label: '减速系数', hint: '命中后速度倍率：0.7 表示降速 30%' },
  slowDuration: { label: '减速持续', hint: '减速/冻结效果的持续时长（秒）' },
  freezeEvery: { label: '冻结节奏', hint: '满级时每 N 次脉冲冻结一次' },
  freezeDuration: { label: '冻结持续', hint: '冻结状态的持续时长（秒）' },
  // —— 等离子炮 ——
  projectileSpeed: { label: '弹速', hint: '炮弹飞行速度（像素/秒）' },
  splashRadius: { label: '溅射半径', hint: '炮弹命中点周围造成伤害的范围' },
  splashFactor: { label: '溅射比例', hint: '溅射伤害 = 主伤害 × 该比例' },
  splashRadiusMul: { label: '满级溅射半径乘', hint: '满级时溅射半径的相对倍率' },
  splashFactorMul: { label: '满级溅射比例乘', hint: '满级时溅射比例的相对倍率' },
  // —— 特斯拉塔 ——
  chainCount: { label: '弹射数', hint: '主目标之外额外弹射的目标数量' },
  chainRange: { label: '弹射半径', hint: '搜索下一个弹射目标的半径' },
  chainFalloff: { label: '弹射衰减', hint: '每次弹射伤害保留的比例（0.62=衰减 38%）' },
  chainCountBonus: { label: '满级+弹射', hint: '满级时额外增加的弹射目标数' },
  chainRangeMul: { label: '满级弹射半径乘', hint: '满级时弹射半径的相对倍率' },
  // —— 电磁炮 ——
  penetrate: { label: '穿透数', hint: '主目标之外额外穿透的目标数量' },
  ignoreArmor: { label: '无视护甲', hint: '造成伤害时无视护甲的比例（0.4=无视 40%）' },
  penetrateBonus: { label: '满级+穿透', hint: '满级时额外增加的穿透目标数' },
  aimTolerance: { label: '对准阈值', hint: '炮口与目标夹角小于该弧度（弧度）才开火' },
  maxDistanceMul: { label: '弹道长度乘', hint: '弹道最大长度 = 射程 × 该值' },
  // —— 敌人 ——
  hp: { label: '生命', hint: '敌人总生命值' },
  speed: { label: '速度', hint: '敌人移动速度（像素/秒）' },
  armor: { label: '护甲', hint: '减伤比例（0~1），越高越抗打' },
  reward: { label: '奖励金币', hint: '击杀后获得的金币' },
  radius: { label: '碰撞半径', hint: '命中判定与渲染半径' },
  regen: { label: '每秒回血', hint: '存活时每秒恢复的生命' },
  // —— 敌人阶段 ——
  at: { label: '触发阈值', hint: '生命百分比降到该值（0~1）时进入此阶段' },
  speedMul: { label: '阶段速度乘', hint: '该阶段速度相对基础的倍数' },
  armorBonus: { label: '阶段护甲加', hint: '该阶段额外叠加的护甲比例' },
};

/** 取字段的中文标签与提示；未知 key 回退到原样 */
function fieldInfo(key) {
  const info = FIELD_INFO[key];
  return info ? { label: info.label, hint: info.hint } : { label: key, hint: '' };
}

/** 数值步长：比例/系数类用小数步长，其余整数 */
function stepFor(key) {
  if (key === 'fireInterval') return 0.01;
  if (/armor|Factor|Mul|falloff|Slow|firingFx|speedMul|regen|at|ignoreArmor/i.test(key)) return 0.05;
  return 1;
}

function waveStep(key) {
  if (key === 'hpGrowthPerWave') return 0.01;
  if (key === 'intervalScale') return 0.05;
  if (key === 'enemyCountScale') return 0.1;
  return 1;
}

function globalStep(key) {
  if (key === 'orbitalDamage') return 10;
  if (key === 'orbitalRadius') return 1;
  if (key === 'empFreezeDuration') return 0.1;
  return 1; // shieldAmount
}

function makeBtn(label, fn) {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'ops-btn';
  b.textContent = label;
  b.addEventListener('click', fn);
  return b;
}

export class OpsPanel {
  constructor(balance) {
    this.balance = balance;
    this.root = null;
    this.body = null;
    this.io = null;
    this.status = null;
  }

  /** 仅 ?ops=1 时调用 */
  init() {
    this._injectStyles();
    this._build();
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Backquote') {
        e.preventDefault();
        this.toggle();
      }
    });
  }

  toggle() {
    if (this.root) this.root.classList.toggle('hidden');
  }

  _injectStyles() {
    if (document.getElementById('ops-styles')) return;
    const css = `
.ops-panel{position:fixed;top:0;right:0;width:340px;max-width:92vw;height:100%;overflow:auto;z-index:1000;background:rgba(8,16,30,.96);color:#cfe6ff;padding:12px;box-sizing:border-box;font:13px/1.4 system-ui,sans-serif;border-left:1px solid #1d3a5c}
.ops-panel.hidden{display:none}
.ops-header{display:flex;justify-content:space-between;align-items:center}
.ops-header h3{margin:0;color:#00ffff}
.ops-x{background:none;border:none;color:#9fc;font-size:18px;cursor:pointer}
.ops-actions{display:flex;flex-wrap:wrap;gap:6px;margin:8px 0}
.ops-btn{flex:1 1 auto;background:#10263f;color:#cfe6ff;border:1px solid #1d3a5c;border-radius:6px;padding:6px 8px;cursor:pointer;font-size:12px}
.ops-btn:hover{background:#16324f}
.ops-status{font-size:11px;color:#7fd;min-height:14px;margin-bottom:6px}
.ops-section{border:1px solid #1d3a5c;border-radius:6px;margin-bottom:8px;padding:4px 8px}
.ops-section summary{cursor:pointer;font-weight:bold;color:#9fe;padding:4px 0}
.ops-tower{margin:6px 0;padding:6px;border:1px dashed #244;border-radius:6px}
.ops-subtitle{color:#ffd;font-weight:bold;margin-bottom:4px}
.ops-phase-title{color:#f9a;font-size:12px;margin-top:4px}
.ops-phase{color:#f9a;font-size:11px;opacity:.85;margin-left:8px}
.ops-row{display:flex;align-items:center;justify-content:space-between;gap:8px;margin:3px 0}
.ops-row--ind{margin-left:14px}
.ops-label{flex:1;font-size:12px}
.ops-row input{width:86px;background:#0a1626;color:#cfe6ff;border:1px solid #2a4a6e;border-radius:4px;padding:3px 5px}
.ops-io{width:100%;height:96px;margin-top:8px;background:#0a1626;color:#9fdcff;border:1px solid #2a4a6e;border-radius:6px;font-family:monospace;font-size:11px;box-sizing:border-box}
.ops-fab{position:fixed;left:10px;bottom:10px;z-index:1001;width:42px;height:42px;border-radius:50%;background:#10263f;color:#00ffff;border:1px solid #1d3a5c;font-size:20px;cursor:pointer}
`;
    const style = document.createElement('style');
    style.id = 'ops-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  _build() {
    const panel = document.createElement('div');
    panel.id = 'ops-panel';
    panel.className = 'ops-panel hidden';

    const header = document.createElement('div');
    header.className = 'ops-header';
    const title = document.createElement('h3');
    title.textContent = '运营调参';
    const close = document.createElement('button');
    close.className = 'ops-x';
    close.type = 'button';
    close.textContent = '✕';
    close.addEventListener('click', () => this.toggle());
    header.append(title, close);
    panel.appendChild(header);

    const actions = document.createElement('div');
    actions.className = 'ops-actions';
    actions.append(
      makeBtn('应用并保存', () => { this.balance.persist(); this._status('已保存'); }),
      makeBtn('恢复默认', () => { this.balance.resetAll(); this._renderBody(); this._status('已恢复默认'); }),
      makeBtn('导出', () => { this.io.value = this.balance.exportJSON(); this._status('已导出到下方文本框'); }),
      makeBtn('导入', () => {
        try {
          this.balance.importJSON(this.io.value);
          this._renderBody();
          this._status('已导入');
        } catch (err) {
          this._status('导入失败：' + err.message);
        }
      }),
    );
    panel.appendChild(actions);

    this.status = document.createElement('div');
    this.status.className = 'ops-status';
    panel.appendChild(this.status);

    this.body = document.createElement('div');
    this.body.className = 'ops-body';
    panel.appendChild(this.body);

    this.io = document.createElement('textarea');
    this.io.className = 'ops-io';
    this.io.placeholder = '导出 / 导入预设 JSON';
    panel.appendChild(this.io);

    document.body.appendChild(panel);
    this.root = panel;
    this._renderBody();

    const fab = document.createElement('button');
    fab.id = 'ops-fab';
    fab.className = 'ops-fab';
    fab.type = 'button';
    fab.textContent = '⚙';
    fab.title = '运营调参（` 键）';
    fab.addEventListener('click', () => this.toggle());
    document.body.appendChild(fab);
  }

  _status(msg) {
    if (this.status) this.status.textContent = msg;
  }

  _renderBody() {
    this.body.innerHTML = '';
    this._section('防御塔', (sec) => this._renderTowers(sec));
    this._section('敌人', (sec) => this._renderEnemies(sec));
    this._section('波次', (sec) => this._renderWaves(sec));
    this._section('全局', (sec) => this._renderGlobal(sec));
  }

  _section(title, fill) {
    const d = document.createElement('details');
    d.className = 'ops-section';
    d.open = true;
    const sum = document.createElement('summary');
    sum.textContent = title;
    d.appendChild(sum);
    const inner = document.createElement('div');
    inner.className = 'ops-section-body';
    d.appendChild(inner);
    fill(inner);
    this.body.appendChild(d);
  }

  _renderTowers(parent) {
    for (const t of TOWER_TYPES) {
      const blk = document.createElement('div');
      blk.className = 'ops-tower';
      const h = document.createElement('div');
      h.className = 'ops-subtitle';
      h.textContent = `${t.icon} ${t.name}`;
      blk.appendChild(h);
      parent.appendChild(blk);

      const stats = this.balance.getTowerStats(t.id, t.stats);
      for (const key of Object.keys(stats)) {
        if (key === 'cost') continue; // 售价单独渲染，避免与覆盖层重复
        const info = fieldInfo(key);
        this._row(blk, info.label, stats[key],
          { step: stepFor(key), hint: info.hint, rawKey: key },
          (v) => this.balance.update('towers', t.id, key, v));
      }

      // 售价（独立字段，不在 stats 内）
      const cInfo = fieldInfo('cost');
      this._row(blk, cInfo.label, this.balance.getTowerCost(t.id, t.cost),
        { step: 5, hint: cInfo.hint, rawKey: 'cost' },
        (v) => this.balance.update('towers', t.id, 'cost', v));
    }
  }

  _renderEnemies(parent) {
    for (const e of Object.values(EnemyTypes)) {
      const blk = document.createElement('div');
      blk.className = 'ops-tower';
      const h = document.createElement('div');
      h.className = 'ops-subtitle';
      h.textContent = `${e.icon || ''} ${e.name}`;
      blk.appendChild(h);
      parent.appendChild(blk);

      const cfg = this.balance.getEnemyConfigMerged(e.id, EnemyTypes[e.id]);
      for (const key of ['hp', 'speed', 'armor', 'reward', 'radius', 'regen']) {
        if (cfg[key] == null) continue;
        const info = fieldInfo(key);
        this._row(blk, info.label, cfg[key],
          { step: stepFor(key), hint: info.hint, rawKey: key },
          (v) => this.balance.update('enemies', e.id, key, v));
      }

      if (Array.isArray(cfg.phases)) {
        const ph = document.createElement('div');
        ph.className = 'ops-phase-title';
        ph.textContent = '阶段';
        blk.appendChild(ph);
        cfg.phases.forEach((p, i) => {
          const lbl = document.createElement('div');
          lbl.className = 'ops-phase';
          lbl.textContent = `· ${p.name || ('阶段' + (i + 1))} @${p.at}`;
          blk.appendChild(lbl);
          for (const k of ['at', 'speedMul', 'regen', 'armorBonus']) {
            if (p[k] == null) continue;
            const info = fieldInfo(k);
            this._row(blk, info.label, p[k],
              { step: stepFor(k), indent: true, hint: info.hint, rawKey: k },
              (v) => this.balance.update('enemies', e.id, k, v, i));
          }
        });
      }
    }
  }

  _renderWaves(parent) {
    const opts = this.balance.getWaveOptions();
    for (const key of Object.keys(WAVE_LABELS)) {
      this._row(parent, WAVE_LABELS[key], opts[key],
        { step: waveStep(key), hint: WAVE_HINTS[key] || '', rawKey: key },
        (v) => this.balance.update('waves', null, key, v));
    }
  }

  /** 全局战斗数值（技能相关）：轨道打击伤害/半径、护盾量、EMP 冻结时长 */
  _renderGlobal(parent) {
    const g = this.balance.getGlobal();
    for (const key of Object.keys(GLOBAL_INFO)) {
      const info = GLOBAL_INFO[key];
      this._row(parent, info.label, g[key],
        { step: globalStep(key), hint: info.hint, rawKey: key },
        (v) => this.balance.update('global', null, key, v));
    }
  }

  _row(parent, label, value, { step = 1, indent = false, hint = '', rawKey = '' } = {}, onInput) {
    const row = document.createElement('label');
    row.className = 'ops-row' + (indent ? ' ops-row--ind' : '');
    // 悬浮提示：原始字段名 + 中文说明，方便核对导出 JSON 与调参
    row.title = (rawKey ? `[${rawKey}] ` : '') + (hint || '');

    const span = document.createElement('span');
    span.className = 'ops-label';
    span.textContent = label;

    const input = document.createElement('input');
    input.type = 'number';
    input.value = value;
    input.step = step;
    input.min = 0;
    input.addEventListener('input', () => {
      const v = Number(input.value);
      if (Number.isFinite(v)) onInput(v);
    });
    // 失焦时落盘，避免每次按键都写 localStorage
    input.addEventListener('change', () => this.balance.persist());

    row.append(span, input);
    parent.appendChild(row);
  }
}
